/**
 * @file Parent-board view dispatch helpers shared by current board views.
 *
 * This module owns the behavior-preserving shell sequence for registered
 * parent boards while callers keep renderer and action adapters at the legacy
 * compatibility boundary.
 */

import {
  state,
  getEffectiveViewLayout,
} from "../../app/state/state.js";
import {
    filterTasksByVisibilityHorizon,
} from "../utils/taskVisibility.js";
import {
    getUsersTasks,
} from "../utils/taskDisplay.js";
import { renderBoardControlBar } from "./boardControls.js";
import { hasActiveRuntimeControls } from "./boardRuntime.js";
import { getBoardRegistryEntry } from "./boardRegistry.js";
import {
  syncMobileRouteHeaderActionSlots,
} from "../ui/mobileRouteHeaderActions.js";

function getBoardElements(itemType) {
  return {
    board: document.getElementById(`${itemType}-board`),
    controls: document.getElementById(`${itemType}-board-controls`),
  };
}

function getViewItems(viewKey) {
  if (viewKey !== "tasks") {
    return null;
  }

  const rawUserTasks = getUsersTasks();
  return hasActiveRuntimeControls("tasks") ?
    rawUserTasks :
    filterTasksByVisibilityHorizon(rawUserTasks);
}

function attachQuickCloseHandlers(board, viewKey, onQuickCloseTask) {
  if (viewKey !== "tasks") {
    return;
  }

  if (typeof onQuickCloseTask !== "function") {
    return;
  }

  board.querySelectorAll(".task-quick-close-btn").forEach((button) => {
    button.onclick = async (event) => {
      event.stopPropagation();
      button.disabled = true;
      try {
        await onQuickCloseTask(button.dataset.taskId);
      } catch (error) {
        console.error("Failed to quick close task:", error);
      } finally {
        button.disabled = false;
      }
    };
  });
}

function attachBoardItemClickHandlers(board, entry, callbacks) {
  const detailsEnabled = entry?.availability?.detailRoutesEnabled === true;
  board.querySelectorAll(".kanban-card, tr[data-id]").forEach((element) => {
    element.onclick = null;
    element.onclick = (event) => {
      const quickAddButton = event.target.closest(".quick-add-task-btn");

      if (quickAddButton && entry.capabilities.supportsQuickAddTask) {
        event.stopPropagation();
        callbacks.openTaskForm({
          parentId: quickAddButton.dataset[`${entry.itemType}Id`],
          owner: state.currentUser?.uid,
        });
        return;
      }

      if (event.target.closest("button")) {
        return;
      }

      if (detailsEnabled) {
        callbacks.openDetails(element.dataset.id);
      }
    };
  });
}

function renderBoardContent(board, viewKey, items, renderers) {
  if (getEffectiveViewLayout(viewKey) === "table") {
    renderers.table(board, items);
    return;
  }

  if (state.showWIP && typeof renderers.wipKanban === "function") {
    renderers.wipKanban(board, items);
    return;
  }

  renderers.kanban(board, items);
}

/**
 * Renders one registered parent board through the shared board shell flow.
 * @param {string} viewKey - Registered parent-board view key.
 * @param {object} adapters - Renderer and action adapters owned by the caller.
 * @param {Function} adapters.renderFooterToggle - Footer layout toggle renderer.
 * @param {Function} adapters.openDetails - Detail-opening callback.
 * @param {Function} adapters.openTaskForm - Task form-opening callback.
 * @param {Function} [adapters.onQuickCloseTask] - Task quick-close callback.
 * @param {object} adapters.renderers - Board renderer callbacks.
 * @param {Function} adapters.renderers.table - Table renderer.
 * @param {Function} adapters.renderers.kanban - Kanban renderer.
 * @param {Function} [adapters.renderers.wipKanban] - WIP kanban renderer.
 * @returns {boolean} True when a registered parent board was rendered.
 */
export function renderParentBoardView(viewKey, adapters) {
  const entry = getBoardRegistryEntry(viewKey);
  if (!entry) {
    return false;
  }

  adapters.renderFooterToggle(entry.itemType);

  const settings = state.viewSettings?.[viewKey];
  if (!settings) {
    console.error(`${entry.display.singularLabel} view settings not loaded.`);
    return true;
  }

  const { board, controls } = getBoardElements(entry.itemType);
  if (!board || !controls) {
    console.error(`${entry.display.singularLabel} view elements not found.`);
    return true;
  }

  syncMobileRouteHeaderActionSlots(document.getElementById(`${viewKey}-view`));
  renderBoardControlBar(viewKey);

  const items = getViewItems(viewKey);
  board.innerHTML = "";
  renderBoardContent(board, viewKey, items, adapters.renderers);
  attachBoardItemClickHandlers(board, entry, adapters);
  attachQuickCloseHandlers(board, viewKey, adapters.onQuickCloseTask);

  return true;
}
